/**
 * <ck-filter-pill> — toggleable dropdown pill in the collection toolbar.
 *
 * Theme integration:
 *   - The form inside this component carries class="filter-form", which
 *     theme.js auto-binds to onFormSubmit (AJAX-refreshes #CollectionAjaxContent).
 *   - After AJAX, the component is destroyed + recreated. We persist
 *     "which pill was last interacted with" in a module-level variable so the
 *     same pill re-opens itself on reconnect.
 */

if (!customElements.get('ck-filter-pill')) {
  // Module-scoped: persists across AJAX re-renders within the same SPA-ish session.
  let lastInteractedBlockId = null;
  const PANEL_TRANSITION_MS = 260;

  // Single open at a time — broadcast event so siblings can close themselves.
  const PILL_OPEN_EVENT = 'ck-filter-pill:open';

  customElements.define(
    'ck-filter-pill',
    class CkFilterPill extends HTMLElement {
      connectedCallback() {
        this.trigger = this.querySelector('.ck-filter-pill');
        const panelId = this.trigger?.getAttribute('aria-controls');
        this.panel = panelId ? document.getElementById(panelId) : null;
        this.doneBtn = this.panel?.querySelector('[data-ck-pill-close]');
        this.form = this.panel?.querySelector('form.filter-form');
        this.blockId = this.dataset.blockId;

        if (!this.trigger || !this.panel) return;

        this._onTriggerClick = this._onTriggerClick.bind(this);
        this._onDoneClick = this._onDoneClick.bind(this);
        this._onDocumentClick = this._onDocumentClick.bind(this);
        this._onKeydown = this._onKeydown.bind(this);
        this._onFormChange = this._onFormChange.bind(this);
        this._onSiblingOpen = this._onSiblingOpen.bind(this);

        this.trigger.addEventListener('click', this._onTriggerClick);
        if (this.doneBtn) this.doneBtn.addEventListener('click', this._onDoneClick);
        if (this.form) this.form.addEventListener('change', this._onFormChange);
        document.addEventListener(PILL_OPEN_EVENT, this._onSiblingOpen);

        // Re-open after AJAX refresh if we were the last interacted pill.
        if (lastInteractedBlockId && lastInteractedBlockId === this.blockId) {
          // Defer so theme.sections.reinit finishes binding the form first.
          requestAnimationFrame(() => this.open());
        }
      }

      disconnectedCallback() {
        this._stopPanelAnimation();
        if (this.trigger) this.trigger.removeEventListener('click', this._onTriggerClick);
        if (this.doneBtn) this.doneBtn.removeEventListener('click', this._onDoneClick);
        if (this.form) this.form.removeEventListener('change', this._onFormChange);
        document.removeEventListener(PILL_OPEN_EVENT, this._onSiblingOpen);
        document.removeEventListener('click', this._onDocumentClick);
        document.removeEventListener('keydown', this._onKeydown);
      }

      get isOpen() {
        return this.trigger.getAttribute('aria-expanded') === 'true';
      }

      open() {
        if (this.isOpen) return;

        // Tell sibling pills to close.
        document.dispatchEvent(
          new CustomEvent(PILL_OPEN_EVENT, { detail: { blockId: this.blockId } }),
        );

        this._stopPanelAnimation();
        this.panel.hidden = false;
        this.panel.classList.remove('is-closing');
        this.panel.classList.add('is-opening');

        this.panel.style.maxHeight = '0px';
        this.panel.offsetHeight;
        this.panel.style.maxHeight = `${this.panel.scrollHeight}px`;
        this.trigger.setAttribute('aria-expanded', 'true');

        this._panelTransitionEnd = () => {
          if (this._closeFallbackTimer) {
            window.clearTimeout(this._closeFallbackTimer);
            this._closeFallbackTimer = null;
          }
          this.panel.classList.remove('is-opening');
          this.panel.classList.add('is-open');
          this.panel.style.maxHeight = 'none';
          this.panel.removeEventListener('transitionend', this._panelTransitionEnd);
          this._panelTransitionEnd = null;
        };
        this.panel.addEventListener('transitionend', this._panelTransitionEnd);

        // Bind global listeners only while open.
        // Defer document click binding by one frame so the click that opened us
        // doesn't immediately close us via the outside-click handler.
        requestAnimationFrame(() => {
          document.addEventListener('click', this._onDocumentClick);
        });
        document.addEventListener('keydown', this._onKeydown);
      }

      close({ returnFocus = false } = {}) {
        if (!this.isOpen) return;

        this._stopPanelAnimation();
        this.panel.classList.remove('is-opening', 'is-open');
        this.panel.classList.add('is-closing');

        // Start from current rendered height, then collapse to 0.
        if (this.panel.style.maxHeight === 'none' || !this.panel.style.maxHeight) {
          this.panel.style.maxHeight = `${this.panel.scrollHeight}px`;
        }
        this.panel.offsetHeight;
        this.panel.style.maxHeight = '0px';

        this._panelTransitionEnd = () => {
          if (this._closeFallbackTimer) {
            window.clearTimeout(this._closeFallbackTimer);
            this._closeFallbackTimer = null;
          }
          this.panel.hidden = true;
          this.panel.classList.remove('is-closing');
          this.panel.style.maxHeight = '';
          this.panel.removeEventListener('transitionend', this._panelTransitionEnd);
          this._panelTransitionEnd = null;
        };
        this.panel.addEventListener('transitionend', this._panelTransitionEnd);

        this._closeFallbackTimer = window.setTimeout(() => {
          if (!this.panel.hidden) {
            this.panel.hidden = true;
            this.panel.classList.remove('is-closing');
            this.panel.style.maxHeight = '';
          }
        }, PANEL_TRANSITION_MS + 60);
        this.trigger.setAttribute('aria-expanded', 'false');

        document.removeEventListener('click', this._onDocumentClick);
        document.removeEventListener('keydown', this._onKeydown);

        if (returnFocus) this.trigger.focus();
      }

      toggle() {
        if (this.isOpen) this.close({ returnFocus: true });
        else this.open();
      }

      _onTriggerClick(evt) {
        evt.preventDefault();
        this.toggle();
      }

      _onDoneClick(evt) {
        evt.preventDefault();
        this.close({ returnFocus: true });
      }

      _onDocumentClick(evt) {
        if (this.contains(evt.target)) return;
        if (this.panel && this.panel.contains(evt.target)) return;
        this.close();
      }

      _onKeydown(evt) {
        if (evt.key === 'Escape') {
          evt.stopPropagation();
          this.close({ returnFocus: true });
        }
      }

      _onFormChange() {
        // Remember us so the next mount (post-AJAX) can re-open this pill.
        lastInteractedBlockId = this.blockId;
      }

      _onSiblingOpen(evt) {
        if (evt.detail.blockId === this.blockId) return;
        this.close();
      }

      _stopPanelAnimation() {
        if (!this.panel) return;
        if (this._panelTransitionEnd) {
          this.panel.removeEventListener('transitionend', this._panelTransitionEnd);
          this._panelTransitionEnd = null;
        }
        if (this._closeFallbackTimer) {
          window.clearTimeout(this._closeFallbackTimer);
          this._closeFallbackTimer = null;
        }
      }
    },
  );
}
